// Service Worker registration for better caching and performance

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  // Check for service worker bypass
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('nosw') === '1') {
    console.log('🚫 Service Worker bypassed via ?nosw=1');
    return;
  }

  // Skip SW on Lovable preview domains to avoid caching issues during development
  const hostname = window.location.hostname;
  if (hostname.endsWith('.lovableproject.com') || hostname.endsWith('.lovable.app')) {
    console.log('🚫 Service Worker skipped on preview domain:', hostname);
    return;
  }

  // Skip SW on Print Bridge and Settings pages to prevent refresh issues
  const pathname = window.location.pathname;
  if (pathname === '/print-bridge' || pathname === '/settings') {
    console.log('🚫 Service Worker skipped on protected page to prevent refresh:', pathname);
    return;
  }

  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(import.meta.env.BASE_URL || '/', window.location.href);
    
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const baseUrl = import.meta.env.BASE_URL || '/';
      const swUrl = `${baseUrl}sw.js`.replace('//', '/');

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('🔧 This web app is being served cache-first by a service worker');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('✅ Service Worker registered successfully');
      
      // FORCE IMMEDIATE UPDATE CHECK with error handling
      registration.update().catch(err => {
        console.warn('⚠️ SW update check failed (non-critical):', err.message);
      });
      
      // Reload when the new Service Worker takes control
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        
        // Don't auto-reload on protected pages (Settings, Print Bridge)
        const currentPath = window.location.pathname;
        const protectedPages = ['/print-bridge', '/settings'];
        if (protectedPages.includes(currentPath)) {
          console.log('⚠️ Protected page - deferring reload to preserve state:', currentPath);
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast?.info?.('App update available - refresh when ready');
          }
          return;
        }
        
        // Also check if Print Bridge is active via sessionStorage flag
        const printBridgeActive = sessionStorage.getItem('print_bridge_active');
        if (printBridgeActive === 'true') {
          console.log('⚠️ Print Bridge active - deferring reload to preserve printer connections');
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast?.info?.('App update available - refresh when ready');
          }
          return;
        }
        
        refreshing = true;
        console.log('🔄 New service worker activated. Reloading...');
        window.location.reload();
      });

      const sendSkipWaiting = () => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      };

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('🔄 New service worker available. Clearing caches and activating...');
              
              // Clear all caches before activating new SW
              caches.keys().then(cacheNames => {
                return Promise.all(
                  cacheNames.map(cacheName => {
                    console.log('🗑️ Deleting cache:', cacheName);
                    return caches.delete(cacheName);
                  })
                );
              }).then(() => {
                console.log('✅ All caches cleared. Activating new service worker...');
                sendSkipWaiting();
                config?.onUpdate?.(registration);
              });
            } else {
              console.log('📦 Content is cached for offline use');
              config?.onSuccess?.(registration);
            }
          }
        };
      };

      // If there's already a waiting worker (updated in the background), activate it
      if (registration.waiting) {
        console.log('📦 New Service Worker is waiting; clearing caches and activating…');
        caches.keys().then(cacheNames => {
          return Promise.all(
            cacheNames.map(cacheName => {
              console.log('🗑️ Deleting cache:', cacheName);
              return caches.delete(cacheName);
            })
          );
        }).then(() => {
          console.log('✅ Caches cleared. Activating waiting service worker...');
          sendSkipWaiting();
        });
      }
    })
    .catch((error) => {
      console.error('❌ Error during service worker registration:', error);
      
      // Report to Sentry if available
      if (typeof window !== 'undefined' && (window as any).Sentry) {
        (window as any).Sentry.captureException(error, {
          tags: { errorType: 'service-worker-registration' },
          contexts: { serviceWorker: { url: swUrl } }
        });
      }
      
      // Auto-unregister if SW is broken
      if (error.message?.includes('script') || error.message?.includes('load')) {
        console.log('🔧 Attempting to unregister broken service worker...');
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister().then(() => {
              console.log('✅ Broken SW unregistered');
            });
          });
        });
      }
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('🔌 No internet connection found. App is running in offline mode');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}